import { useEffect, useMemo, useRef, useState } from "react";
import {
  EMPTY_INPUT,
  createMatch,
  restartRound,
  stepMatch,
  type MatchState,
  type PlayerProfile,
  type PlayerSlot,
  type ServerMessage
} from "@marshmallow-duel/simulation";

import { GameAudio } from "../game/audio";
import { InputManager } from "../game/input";
import { NetworkClient } from "../game/network-client";
import { PeerSession } from "../game/peer-session";
import { renderMatch } from "../game/renderer";
import type { GamePreferences } from "../game/preferences";
import { ScaledStage } from "./ScaledStage";

import type { TranslationKey } from "../i18n";

type MatchedMessage = Extract<ServerMessage, { type: "matched" }>;

interface OnlineGameScreenProps {
  readonly match: MatchedMessage;
  readonly network: NetworkClient;
  readonly preferences: GamePreferences;
  readonly t: (key: TranslationKey) => string;
  readonly onLeave: () => void;
}

export const OnlineGameScreen = ({ match, network, preferences, t, onLeave }: OnlineGameScreenProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const profiles = useMemo(() => Object.fromEntries(match.peers.map(({ slot, profile }) => [slot, profile])) as Record<PlayerSlot, PlayerProfile>, [match.peers]);
  const stateRef = useRef<MatchState>(createMatch({ mode: "online", map: match.settings.map, rules: match.settings.rules, profiles, seed: match.seed }));
  const sessionsRef = useRef(new Map<string, PeerSession>());
  const [connectedPeers, setConnectedPeers] = useState(0);
  const [phase, setPhase] = useState(stateRef.current.phase);
  const [roundWinner, setRoundWinner] = useState<PlayerSlot | null>(null);
  const [matchWinner, setMatchWinner] = useState<PlayerSlot | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const isHost = match.hostPeerId === match.peers.find(({ slot }) => slot === match.ownSlot)?.peerId;
  const requiredPeers = match.peers.length - 1;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sessions = sessionsRef.current;
    const localPeer = match.peers.find(({ slot }) => slot === match.ownSlot);
    if (!localPeer) return;
    const remotes = isHost ? match.peers.filter(({ peerId }) => peerId !== localPeer.peerId) : match.peers.filter(({ peerId }) => peerId === match.hostPeerId);
    let active = true;
    let readyCount = 0;
    let previousPhase = stateRef.current.phase;

    const updateUi = () => {
      const state = stateRef.current;
      setPhase(state.phase);
      setRoundWinner(state.roundWinner);
      setMatchWinner(state.matchWinner);
    };

    const continueRound = () => {
      if (!isHost || stateRef.current.phase !== "roundOver") return;
      stateRef.current = restartRound(stateRef.current);
      previousPhase = stateRef.current.phase;
      updateUi();
      for (const session of sessions.values()) session.sendSnapshot(stateRef.current, true);
    };

    for (const remote of remotes) {
      const session = new PeerSession({
        role: isHost ? "host" : "guest",
        remotePeerId: remote.peerId,
        ownSlot: match.ownSlot,
        remoteSlot: remote.slot,
        iceServers: match.iceServers,
        sendSignal: (targetPeerId, signal) => network.sendSignal(targetPeerId, signal)
      });
      session.onConnected = () => {
        if (!active) return;
        readyCount += 1;
        setConnectedPeers(readyCount);
      };
      session.onDisconnected = () => active && setConnectionError(true);
      session.onProtocolMismatch = () => active && setConnectionError(true);
      session.onContinueRequested = continueRound;
      session.onSnapshot = (snapshot) => {
        if (!active || isHost) return;
        stateRef.current = { ...snapshot, map: match.settings.map, rules: match.settings.rules };
        updateUi();
      };
      sessions.set(remote.peerId, session);
    }

    const unsubscribeSignals = network.subscribeSignals((signal) => {
      const session = sessions.get(signal.fromPeerId);
      if (session) void session.handleSignal(signal).catch(() => setConnectionError(true));
    });
    if (isHost) for (const session of sessions.values()) void session.start().catch(() => setConnectionError(true));

    const input = new InputManager(preferences.gamepads);
    const audio = new GameAudio(preferences.sound, preferences.screaming);
    if (preferences.notifier) audio.play("snd_versus");
    if (preferences.music) audio.startMusic((Math.floor(Math.random() * 6) + 1));
    input.attach();
    let frame = 0;
    let previous = performance.now();
    let accumulator = 0;
    let renderedFrames = 0;
    let fpsWindow = previous;
    let fps = 0;
    const loop = (now: number) => {
      accumulator = Math.min(accumulator + now - previous, 500);
      previous = now;
      while (accumulator >= 1000 / stateRef.current.roomSpeed) {
        const tickDuration = 1000 / stateRef.current.roomSpeed;
        const localInput = input.read(1)[1] ?? EMPTY_INPUT;
        if (readyCount >= requiredPeers) {
          if (isHost) {
            const inputs: Partial<Record<PlayerSlot, typeof EMPTY_INPUT>> = { [match.ownSlot]: localInput };
            for (const session of sessions.values()) inputs[session.remoteSlot] = session.remoteInput ?? EMPTY_INPUT;
            const state = stepMatch(stateRef.current, inputs);
            audio.playEvents(state.events);
            const phaseChanged = state.phase !== previousPhase;
            if (phaseChanged) {
              previousPhase = state.phase;
              updateUi();
            }
            if (state.tick % 2 === 0 || phaseChanged) for (const session of sessions.values()) session.sendSnapshot(state, phaseChanged);
          } else {
            sessions.get(match.hostPeerId)?.sendInput(stateRef.current.tick, localInput);
            audio.playEvents(stateRef.current.events);
            stateRef.current.events = [];
          }
        }
        accumulator -= tickDuration;
      }
      renderedFrames += 1;
      if (now - fpsWindow >= 500) {
        fps = renderedFrames * 1000 / (now - fpsWindow);
        renderedFrames = 0;
        fpsWindow = now;
      }
      renderMatch(canvas, stateRef.current, { oldTextures: preferences.oldTextures, raining: preferences.raining, showFps: preferences.showFps, fps });
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Escape") leave();
      if (event.code === "Enter" && stateRef.current.phase === "roundOver") {
        if (isHost) continueRound();
        else sessions.get(match.hostPeerId)?.requestContinue();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      active = false;
      cancelAnimationFrame(frame);
      input.detach();
      audio.stopMusic();
      window.removeEventListener("keydown", onKeyDown);
      unsubscribeSignals();
      for (const session of sessions.values()) session.close(false);
      sessions.clear();
    };
  }, [isHost, match, network, preferences, profiles, requiredPeers]);

  const leave = () => {
    network.leaveRoom();
    onLeave();
  };

  const continueRound = () => {
    if (isHost && stateRef.current.phase === "roundOver") {
      stateRef.current = restartRound(stateRef.current);
      setPhase(stateRef.current.phase);
      setRoundWinner(null);
      for (const session of sessionsRef.current.values()) session.sendSnapshot(stateRef.current, true);
    } else if (!isHost) {
      sessionsRef.current.get(match.hostPeerId)?.requestContinue();
    }
  };

  const winner = matchWinner ?? roundWinner;
  return (
    <ScaledStage className="game-stage">
      <canvas ref={canvasRef} width={800} height={600} aria-label={`Marshmallow Duel Reloaded – ${match.settings.map.name}`} />
      {connectedPeers < requiredPeers && !connectionError && <div className="game-intro"><strong>{t("online.connecting")}</strong><p>{connectedPeers}/{requiredPeers}</p></div>}
      {connectionError && <div className="game-results"><strong>{t("online.connectionLost")}</strong><button type="button" onClick={leave}>{t("game.leave")}</button></div>}
      {connectedPeers >= requiredPeers && phase === "countdown" && (
        <div className="game-intro">
          <img src="/assets/original/backgrounds/bg_menu_title.png" alt="Marshmallow Duel Reloaded" />
          <p>{t("game.map")} {match.settings.map.name}</p>
          <strong>{match.peers.map(({ profile }) => profile.name).join(" vs ")}</strong>
        </div>
      )}
      {(phase === "roundOver" || phase === "matchOver") && (
        <div className="game-results">
          <strong>{winner ? `${profiles[winner].name} ${t("game.wins")}` : t("game.draw")}</strong>
          <div>{stateRef.current.players.map(({ slot, profile }) => `${profile.name} ${stateRef.current.scores[slot]}`).join(" : ")}</div>
          {phase === "roundOver" ? <button type="button" onClick={continueRound}>{t("game.continue")}</button> : <button type="button" onClick={leave}>{t("game.leave")}</button>}
        </div>
      )}
      <div className="online-latency">{network.latency} ms</div>
      <button type="button" className="game-leave" onClick={leave}>{t("game.leave")}</button>
    </ScaledStage>
  );
};
